import React, { useState, useRef, useEffect, useCallback } from "react";
import Layout from "../../components/Layout";
import "../../styles/crm/MarketingMail.css";
import authService from "../../services/auth/authService";
import api from "../../services/api";
import {
  fetchEmails,
  fetchEmailDetail,
  envoyerEmail,
  archiverEmails,
  supprimerEmails,
  estimerDestinataires,
  fetchFormationsParType,
  fetchStatutsDisponibles,
  generatePreviewAI,
  generateBodyAI,
} from "../../services/crm/Marketingservice";

// ─── Constantes ───────────────────────────────────────────────────────────────
const STATUTS_PROSPECT_COMPLETS = [
  { label: "🔥 Très intéressé", value: "Très intéressé", order: 1 },
  { label: "📈 Intéressé moyen", value: "Intéressé moyen", order: 2 },
  { label: "👀 Pas intéressé", value: "Pas intéressé", order: 3 },
  { label: "❄️ Perdu", value: "Perdu", order: 4 },
];

const STATUTS_CACHE_KEY = "marketing_mail_statuts_cache";
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// ⚠️ Les onglets affichent "Attestés" mais la valeur reste "Diplômés" pour le filtrage
const TABS = [
  { id: "tous",      label: "Tous les e-mails" },
  { id: "Prospects", label: "Prospects" },
  { id: "Étudiants", label: "Étudiants" },
  { id: "Diplômés",  label: "Attestés" },  // Affichage "Attestés" mais valeur "Diplômés"
  { id: "direct",    label: "Une seule adresse" },
  { id: "archive",   label: "Archivé" },
];

const EMAIL_TEMPLATES = [
  {
    id: "vide",
    icon: "fa-solid fa-pen-to-square",
    color: "#64748B",
    bgColor: "#F8FAFC",
    title: "Email vide",
    description: "Commencez avec un modèle vierge",
    objet: "",
    apercu: "",
    message: "",
    groupeFiltre: null,
    isProspectOnly: false,
  },
  {
    id: "promo",
    icon: "fa-solid fa-tag",
    color: "#f59e0b",
    bgColor: "#fffbeb",
    title: "Promotion formation",
    description: "Annoncez une offre spéciale sur vos formations",
    objet: "🎓 Offre spéciale : -20% sur nos formations",
    apercu: "Ne manquez pas cette opportunité limitée dans le temps",
    message: `Bonjour,\n\nNous avons une excellente nouvelle pour vous ! 🎉\n\nDans le cadre de notre promotion exceptionnelle, bénéficiez de **20% de réduction** sur l'ensemble de nos formations pendant une durée limitée.\n\nC'est l'occasion idéale pour :\n✅ Développer vos compétences professionnelles\n✅ Obtenir une certification reconnue\n✅ Booster votre carrière\n\n👉 Utilisez le code PROMO20 lors de votre inscription.\n\nN'attendez plus, les places sont limitées !\n\nCordialement,\nL'équipe de formation`,
    groupeFiltre: null,
    isProspectOnly: false,
  },
  {
    id: "nouvelle_formation",
    icon: "fa-solid fa-graduation-cap",
    color: "#22c55e",
    bgColor: "#f0fdf4",
    title: "Nouvelle formation",
    description: "Présentez une nouvelle formation disponible",
    objet: "🆕 Nouvelle formation disponible : [Nom de la formation]",
    apercu: "Découvrez notre toute nouvelle formation et inscrivez-vous dès maintenant",
    message: `Bonjour,\n\nNous sommes ravis de vous annoncer le lancement de notre nouvelle formation : **[Nom de la formation]** 🎓\n\nCette formation a été spécialement conçue pour vous permettre de :\n• Acquérir des compétences recherchées sur le marché\n• Progresser à votre rythme avec un accompagnement personnalisé\n• Obtenir une certification reconnue par les employeurs\n\n📅 Début des cours : [Date]\n⏱️ Durée : [Durée]\n📍 Format : [Présentiel / En ligne]\n\nLes inscriptions sont ouvertes ! Rejoignez la première promotion et bénéficiez d'un tarif préférentiel.\n\nCordialement,\nL'équipe pédagogique`,
    groupeFiltre: null,
    isProspectOnly: false,
  },
  {
    id: "evenement",
    icon: "fa-solid fa-calendar-star",
    color: "#6366f1",
    bgColor: "#eef2ff",
    title: "Invitation événement",
    description: "Invitez vos contacts à un événement",
    objet: "📅 Vous êtes invité(e) à [Nom de l'événement]",
    apercu: "Une invitation exclusive rien que pour vous",
    message: `Bonjour,\n\nNous avons le plaisir de vous inviter à notre prochain événement : **[Nom de l'événement]** 🎉\n\n📅 Date : [Date]\n🕐 Heure : [Heure]\n📍 Lieu : [Adresse / Lien]\n\nAu programme :\n• [Point 1]\n• [Point 2]\n• [Point 3]\n\nCet événement est l'occasion de rencontrer nos formateurs, d'échanger avec d'autres professionnels et de découvrir nos nouvelles offres de formation.\n\n🔖 Inscription gratuite mais obligatoire — les places sont limitées.\n\nNous espérons vous y voir nombreux !\n\nCordialement,\nL'équipe organisatrice`,
    groupeFiltre: null,
    isProspectOnly: false,
  },
  {
    id: "relance",
    icon: "fa-solid fa-rotate-right",
    color: "#ef4444",
    bgColor: "#fef2f2",
    title: "Relance prospect",
    description: "Recontactez des prospects inactifs",
    objet: "Avez-vous eu le temps de réfléchir à votre formation ? 🤔",
    apercu: "Nous pensons encore à vous — votre projet nous tient à cœur",
    message: `Bonjour,\n\nJe me permets de vous recontacter suite à votre précédent intérêt pour nos formations.\n\nNous pensons à vous et souhaitons savoir si vous avez eu l'occasion de réfléchir à votre projet de formation.\n\nSi vous avez des questions ou si vous souhaitez en savoir plus, n'hésitez pas à nous contacter. Nous sommes disponibles pour vous accompagner dans votre démarche.\n\n🎁 En guise de geste commercial, nous vous offrons une **session de conseil gratuite** de 30 minutes avec l'un de nos conseillers pédagogiques.\n\nPour en profiter, répondez simplement à cet email ou appelez-nous au [Numéro].\n\nCordialement,\nL'équipe commerciale`,
    groupeFiltre: "Prospects",
    isProspectOnly: true,
  },
  {
    id: "nouvelle_session",
    icon: "fa-solid fa-layer-group",
    color: "#4aa3c7",
    bgColor: "#e8f5fb",
    title: "Nouvelle session formation",
    description: "Annoncez l'ouverture d'une nouvelle session",
    objet: "🗓️ Nouvelle session ouverte — Inscrivez-vous avant le [Date limite]",
    apercu: "Une nouvelle session démarre bientôt, réservez votre place",
    message: `Bonjour,\n\nBonne nouvelle ! Une nouvelle session de formation est désormais disponible. 📚\n\n**Formation :** [Nom de la formation]\n**Dates :** Du [Date début] au [Date fin]\n**Horaires :** [Horaires]\n**Format :** [Présentiel / Distanciel / Hybride]\n\n⚠️ Les places sont limitées — les inscriptions seront clôturées le **[Date limite]**.\n\nPourquoi choisir cette session ?\n✓ Formateurs expérimentés et certifiés\n✓ Programme mis à jour selon les dernières tendances\n✓ Support pédagogique complet fourni\n✓ Certificat délivré à l'issue de la formation\n\nPour vous inscrire ou obtenir plus d'informations, répondez à cet email ou contactez-nous directement.\n\nÀ très bientôt,\nL'équipe de formation`,
    groupeFiltre: null,
    isProspectOnly: false,
  },
];

// ─── Composant Chip pour formations ──────────────────────────────────────────
function ChipGroupFormations({ items, selected, onChange, title, loading }) {
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = searchTerm
    ? items.filter((i) => i.intitule?.toLowerCase().includes(searchTerm.toLowerCase()))
    : items;

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  if (loading) {
    return (
      <div className="ce-sub-block">
        <label className="ce-sub-lbl">{title}</label>
        <div style={{ padding: "12px", textAlign: "center", color: "#94A3B8" }}>
          <i className="fa-solid fa-spinner fa-spin" /> Chargement des formations...
        </div>
      </div>
    );
  }

  return (
    <div className="ce-sub-block">
      <label className="ce-sub-lbl">{title}</label>
      <div style={{ marginBottom: "8px", position: "relative" }}>
        <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: "#94A3B8" }} />
        <input
          type="text"
          placeholder={`Rechercher ${title.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: "100%", padding: "6px 10px 6px 28px", border: "1px solid #E2E8F0", borderRadius: "6px", fontSize: "11px", outline: "none", fontFamily: "inherit" }}
        />
      </div>
      <div className="ce-chips-scroll">
        {items.length === 0 ? (
          <div style={{ padding: "8px", textAlign: "center", color: "#94A3B8", fontSize: "11px" }}>Aucune formation disponible</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "8px", textAlign: "center", color: "#94A3B8", fontSize: "11px" }}>Aucun résultat</div>
        ) : filtered.map((item) => (
          <label key={item.id} className={`ce-chip ${selected.includes(item.id) ? "ce-chip-on" : ""}`}>
            <input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggle(item.id)} />
            {item.intitule}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Composant Chip pour sessions ──────────────────────────────────────────
function ChipGroupSessions({ items, selected, onChange, title, loading }) {
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = searchTerm
    ? items.filter((i) => i.intitule?.toLowerCase().includes(searchTerm.toLowerCase()))
    : items;

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  if (loading) {
    return (
      <div className="ce-sub-block">
        <label className="ce-sub-lbl">{title}</label>
        <div style={{ padding: "12px", textAlign: "center", color: "#94A3B8" }}>
          <i className="fa-solid fa-spinner fa-spin" /> Chargement des sessions...
        </div>
      </div>
    );
  }

  return (
    <div className="ce-sub-block">
      <label className="ce-sub-lbl">{title}</label>
      <div style={{ marginBottom: "8px", position: "relative" }}>
        <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: "#94A3B8" }} />
        <input
          type="text"
          placeholder="Rechercher une session..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: "100%", padding: "6px 10px 6px 28px", border: "1px solid #E2E8F0", borderRadius: "6px", fontSize: "11px", outline: "none", fontFamily: "inherit" }}
        />
      </div>
      <div className="ce-chips-scroll">
        {items.length === 0 ? (
          <div style={{ padding: "8px", textAlign: "center", color: "#94A3B8", fontSize: "11px" }}>
            Aucune session disponible
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "8px", textAlign: "center", color: "#94A3B8", fontSize: "11px" }}>Aucun résultat</div>
        ) : filtered.map((item) => (
          <label key={item.id} className={`ce-chip ${selected.includes(item.id) ? "ce-chip-on" : ""}`}>
            <input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggle(item.id)} />
            {item.intitule}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Fonction pour obtenir la couleur d'un niveau ─────────────────────────────
const getNiveauColor = (niveau) => {
  if (!niveau) return { background: "#F1F5F9", color: "#64748B" };
  if (niveau === "Débutant") return { background: "#DCFCE7", color: "#166534", border: "1px solid #BBF7D0" };
  if (niveau === "Intermédiaire") return { background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" };
  if (niveau === "Avancé") return { background: "#FEE2E2", color: "#991B1B", border: "1px solid #FECACA" };
  return { background: "#F1F5F9", color: "#64748B" };
};

// ─── Composant Chip pour niveaux (avec couleurs) ─────────────────────────────
function ChipGroupNiveaux({ items, selected, onChange, title, loading }) {
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter((x) => x !== val) : [...selected, val]);
  };

  if (loading) {
    return (
      <div className="ce-sub-block">
        <label className="ce-sub-lbl">{title}</label>
        <div style={{ padding: "12px", textAlign: "center", color: "#94A3B8" }}>
          <i className="fa-solid fa-spinner fa-spin" /> Chargement des niveaux...
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="ce-sub-block">
        <label className="ce-sub-lbl">{title}</label>
        <div style={{ padding: "12px", background: "#F0F9FF", borderRadius: "8px", color: "#0284C7", fontSize: "12px", textAlign: "center" }}>
          <i className="fa-solid fa-info-circle" style={{ marginRight: "8px" }} />
          Aucun niveau disponible pour cette formation
        </div>
      </div>
    );
  }

  const sortedItems = [...items].sort((a, b) => (a.order || 99) - (b.order || 99));

  return (
    <div className="ce-sub-block">
      <label className="ce-sub-lbl">{title}</label>
      <div className="ce-chips-scroll">
        {sortedItems.map((item) => {
          const niveauColor = getNiveauColor(item.value);
          return (
            <label 
              key={item.value} 
              className={`ce-chip ${selected.includes(item.value) ? "ce-chip-on" : ""}`}
              style={selected.includes(item.value) ? { 
                background: niveauColor.background, 
                color: niveauColor.color,
                border: `1px solid ${niveauColor.border ? niveauColor.border.split(':')[1] : niveauColor.color}`
              } : {}}
            >
              <input type="checkbox" checked={selected.includes(item.value)} onChange={() => toggle(item.value)} />
              <span style={!selected.includes(item.value) ? niveauColor : {}}>
                {item.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ─── Composant Chip pour statuts ──────────────────────────────────────────
function ChipGroupStatuts({ items, selected, onChange, title, formationSelected }) {
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter((x) => x !== val) : [...selected, val]);
  };

  const sortedItems = [...items].sort((a, b) => (a.order || 99) - (b.order || 99));

  return (
    <div className="ce-sub-block">
      <label className="ce-sub-lbl">
        {title}
        {formationSelected && <span style={{ fontSize: "10px", marginLeft: "8px", color: "#4aa3c7" }}>(filtré par formation)</span>}
      </label>
      <div className="ce-chips-scroll">
        {sortedItems.map((item) => (
          <label key={item.value} className={`ce-chip ${selected.includes(item.value) ? "ce-chip-on" : ""}`}>
            <input type="checkbox" checked={selected.includes(item.value)} onChange={() => toggle(item.value)} />
            {item.label}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Filtre date unique ───────────────────────────────────────────────────────
function DateFilter({ dateValue, setDateValue }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDateValue(newDate);
    setOpen(false);
  };

  const handleReset = () => {
    setDateValue("");
    setOpen(false);
  };

  return (
    <div className="fd-wrap" ref={ref}>
      <button className={`fd-trigger ${dateValue ? "fd-active" : ""}`} onClick={() => setOpen(!open)}>
        <i className="fa-regular fa-calendar" />
        <span>Date</span>
        {dateValue && <span className="fd-count">1</span>}
        {dateValue && (
          <span className="fd-x" onClick={(e) => { e.stopPropagation(); handleReset(); }}>
            <i className="fa-solid fa-xmark" />
          </span>
        )}
      </button>
      {open && (
        <div className="fd-dropdown" style={{ width: "220px" }}>
          <div style={{ padding: "12px" }}>
            <label style={{ fontSize: "11px", color: "#64748B", display: "block", marginBottom: "6px" }}>Filtrer par date</label>
            <input
              type="date"
              className="fd-search-input"
              value={dateValue}
              onChange={handleDateChange}
              style={{ width: "100%", border: "1px solid #E2E8F0", borderRadius: "6px", padding: "7px 9px", fontFamily: "inherit", fontSize: "12.5px" }}
            />
            <div className="fd-foot" style={{ padding: "10px 0 0 0" }}>
              <button className="fd-reset-btn" onClick={handleReset}>Réinitialiser</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filtre date unique pour la modal destinataires ───────────────────────────
function DateSimpleFilter({ dateValue, setDateValue, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleDateChange = (e) => {
    setDateValue(e.target.value);
    setOpen(false);
  };

  return (
    <div className="fd-wrap" ref={ref}>
      <button className={`fd-trigger ${dateValue ? "fd-active" : ""}`} onClick={() => setOpen(!open)}>
        <i className="fa-regular fa-calendar" />
        <span>{label || "Date inscription"}</span>
        {dateValue && <span className="fd-count">1</span>}
        {dateValue && (
          <span className="fd-x" onClick={(e) => { e.stopPropagation(); setDateValue(""); }}>
            <i className="fa-solid fa-xmark" />
          </span>
        )}
      </button>
      {open && (
        <div className="fd-dropdown" style={{ width: "220px" }}>
          <div style={{ padding: "12px" }}>
            <label style={{ fontSize: "11px", color: "#64748B", display: "block", marginBottom: "6px" }}>{label || "Filtrer par date"}</label>
            <input
              type="date"
              className="fd-search-input"
              value={dateValue}
              onChange={handleDateChange}
              style={{ width: "100%", border: "1px solid #E2E8F0", borderRadius: "6px", padding: "7px 9px", fontFamily: "inherit", fontSize: "12.5px" }}
            />
            <div className="fd-foot" style={{ padding: "10px 0 0 0" }}>
              <button className="fd-reset-btn" onClick={() => { setDateValue(""); setOpen(false); }}>Réinitialiser</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fonction pour forcer le téléchargement d'un fichier ──────────────────────
const handleForceDownload = async (fileUrl, fileName) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || fileUrl.split('/').pop() || 'fichier';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erreur lors du téléchargement:', error);
    window.open(fileUrl, '_blank');
  }
};

// ─── Composant pour la liste des destinataires dans le modal ──────────────────
function RecipientList({ destinataires, title }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filteredDestinataires = searchTerm
    ? destinataires.filter(d => 
        d.email_adresse?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.prospect?.nom?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (d.etudiant?.nom?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (d.diplome?.nom?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : destinataires;

  const displayDestinataires = showAll ? filteredDestinataires : filteredDestinataires.slice(0, 3);
  const hasMore = filteredDestinataires.length > 3;

  const getTypeBadgeStyle = (type) => {
    switch(type) {
      case 'prospect': return { background: "#FEF3C7", color: "#D97706" };
      case 'etudiant': return { background: "#DCFCE7", color: "#166534" };
      case 'diplome': return { background: "#E0E7FF", color: "#4338CA" };
      default: return { background: "#F1F5F9", color: "#64748B" };
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'prospect': return "fa-solid fa-user-plus";
      case 'etudiant': return "fa-solid fa-user-graduate";
      case 'diplome': return "fa-solid fa-award";
      default: return "fa-solid fa-envelope";
    }
  };

  const getDisplayName = (dest) => {
    if (dest.prospect) return dest.prospect.nom || dest.prospect.prenom || dest.email_adresse;
    if (dest.etudiant) return dest.etudiant.nom || dest.etudiant.prenom || dest.email_adresse;
    if (dest.diplome) return dest.diplome.nom || dest.diplome.prenom || dest.email_adresse;
    return dest.email_adresse;
  };

  // Affichage du type avec "Attesté" au lieu de "diplome"
  const getDisplayType = (type) => {
    if (type === 'diplome') return 'Attesté';
    return type;
  };

  if (destinataires.length === 0) {
    return (
      <div className="mm-detail-section">
        <div className="mm-detail-section-title">
          <i className="fa-solid fa-users" /> {title}
        </div>
        <div style={{ padding: "16px", textAlign: "center", color: "#94A3B8", background: "#F8FAFC", borderRadius: "12px" }}>
          <i className="fa-regular fa-envelope" style={{ fontSize: "20px", marginBottom: "8px", display: "block" }} />
          Aucun destinataire enregistré
        </div>
      </div>
    );
  }

  return (
    <div className="mm-detail-section">
      <div className="mm-detail-section-title">
        <i className="fa-solid fa-users" /> {title}
        <span style={{ marginLeft: "8px", fontSize: "11px", background: "#e8f5fb", padding: "2px 8px", borderRadius: "20px", color: "#4aa3c7" }}>
          {destinataires.length} contact(s)
        </span>
      </div>

      {/* Barre de recherche */}
      <div style={{ marginBottom: "12px", position: "relative" }}>
        <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "12px", color: "#94A3B8" }} />
        <input
          type="text"
          placeholder="Rechercher par email ou nom..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px 8px 36px",
            border: "1px solid #E2E8F0",
            borderRadius: "10px",
            fontSize: "13px",
            outline: "none",
            fontFamily: "inherit",
            transition: "all 0.2s"
          }}
          onFocus={(e) => e.target.style.borderColor = "#4aa3c7"}
          onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              color: "#94A3B8",
              cursor: "pointer",
              fontSize: "12px"
            }}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      {/* Liste avec scrollbar si plus de 3 */}
      <div 
        style={{ 
          maxHeight: showAll || filteredDestinataires.length > 3 ? "250px" : "auto",
          overflowY: showAll || filteredDestinataires.length > 3 ? "auto" : "visible",
          borderRadius: "10px"
        }}
      >
        {filteredDestinataires.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#94A3B8", background: "#F8FAFC", borderRadius: "10px" }}>
            <i className="fa-solid fa-search" /> Aucun résultat pour "{searchTerm}"
          </div>
        ) : (
          displayDestinataires.map((dest, idx) => (
            <div 
              key={dest.id || idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 12px",
                borderBottom: idx !== displayDestinataires.length - 1 ? "1px solid #F1F5F9" : "none",
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <div style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                background: getTypeBadgeStyle(dest.type_destinataire).background,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}>
                <i className={getTypeIcon(dest.type_destinataire)} style={{ color: getTypeBadgeStyle(dest.type_destinataire).color, fontSize: "14px" }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "13px", fontWeight: "500", color: "#1E293B" }}>
                  {getDisplayName(dest)}
                </div>
                <div style={{ fontSize: "11px", color: "#94A3B8", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <span><i className="fa-regular fa-envelope" style={{ marginRight: "4px" }} />{dest.email_adresse}</span>
                  <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#CBD5E1" }} />
                  <span style={{
                    background: getTypeBadgeStyle(dest.type_destinataire).background,
                    color: getTypeBadgeStyle(dest.type_destinataire).color,
                    padding: "2px 8px",
                    borderRadius: "20px",
                    fontSize: "10px",
                    fontWeight: "500"
                  }}>
                    {getDisplayType(dest.type_destinataire)}
                  </span>
                  {dest.date_envoi && (
                    <>
                      <span style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#CBD5E1" }} />
                      <span><i className="fa-regular fa-calendar" style={{ marginRight: "4px" }} />{new Date(dest.date_envoi).toLocaleDateString("fr-FR")}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bouton Voir plus / Voir moins */}
      {hasMore && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          style={{
            marginTop: "12px",
            padding: "8px 12px",
            background: "white",
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            fontSize: "12px",
            color: "#4aa3c7",
            cursor: "pointer",
            width: "100%",
            fontWeight: "500",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#F8FAFC"; e.currentTarget.style.borderColor = "#4aa3c7"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = "#E2E8F0"; }}
        >
          <i className="fa-solid fa-chevron-down" style={{ marginRight: "6px" }} />
          Voir les {filteredDestinataires.length - 3} autres destinataires
        </button>
      )}

      {showAll && hasMore && (
        <button
          onClick={() => setShowAll(false)}
          style={{
            marginTop: "12px",
            padding: "8px 12px",
            background: "white",
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            fontSize: "12px",
            color: "#64748B",
            cursor: "pointer",
            width: "100%",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#F8FAFC"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
        >
          <i className="fa-solid fa-chevron-up" style={{ marginRight: "6px" }} />
          Réduire la liste
        </button>
      )}
    </div>
  );
}

// ─── Modal Détail email ────────────────────────────────────────────────────────
function EmailDetailModal({ emailId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!emailId) return;
    setLoading(true);
    fetchEmailDetail(emailId)
      .then((data) => { setDetail(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [emailId]);

  const getFileName = (url) => {
    if (!url) return "Fichier joint";
    return url.split('/').pop() || "Fichier joint";
  };

  const isImageFile = (url) => {
    if (!url) return false;
    return url.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);
  };

  return (
    <div className="mm-overlay" onClick={onClose}>
      <div className="mm-modal mm-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mm-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "#e8f5fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="fa-solid fa-envelope" style={{ color: "#4aa3c7", fontSize: "16px" }} />
            </div>
            <div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#1E293B" }}>Détail de l'e-mail</div>
              <div style={{ fontSize: "12px", color: "#94A3B8" }}>Informations complètes de la campagne</div>
            </div>
          </div>
          <button className="mm-modal-close" onClick={onClose}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="mm-modal-body">
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "200px", color: "#94A3B8" }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "24px" }} />
            </div>
          ) : !detail ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94A3B8" }}>Impossible de charger les détails.</div>
          ) : (
            <>
              <div className="mm-detail-meta-grid">
                {[
                  ["Objet", detail.objet],
                  ["Envoyé par", detail.envoye_par_email],
                  ["Groupe cible", detail.groupe_display || detail.email_direct || "—"],
                  ["Destinataires", (detail.nombre_destinataires || 0).toLocaleString() + " contact(s)"],
                  ["Date d'envoi", detail.date_envoi ? new Date(detail.date_envoi).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"],
                  ["Statut", detail.est_archive ? "Archivé" : "Actif"],
                ].map(([k, v]) => (
                  <div key={k} className="mm-detail-meta-item">
                    <div className="mm-detail-meta-key">{k}</div>
                    <div className="mm-detail-meta-val">{v}</div>
                  </div>
                ))}
              </div>

              {detail.apercu && (
                <div className="mm-detail-section">
                  <div className="mm-detail-section-title"><i className="fa-solid fa-eye" /> Texte d'aperçu</div>
                  <div className="mm-detail-apercu">{detail.apercu}</div>
                </div>
              )}

              <div className="mm-detail-section">
                <div className="mm-detail-section-title"><i className="fa-solid fa-align-left" /> Corps du message</div>
                <div className="mm-detail-body">{detail.message}</div>
              </div>

              {detail.fichier_url && (
                <div className="mm-detail-section">
                  <div className="mm-detail-section-title">
                    <i className="fa-solid fa-paperclip" /> Pièce jointe
                  </div>
                  
                  <div style={{ marginBottom: "12px", fontSize: "13px", color: "#64748B", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa-regular fa-file" />
                    <span>{getFileName(detail.fichier_url)}</span>
                  </div>
                  
                  {isImageFile(detail.fichier_url) && (
                    <div style={{ marginBottom: "12px", border: "1px solid #E2E8F0", borderRadius: "8px", overflow: "hidden", maxWidth: "300px" }}>
                      <img 
                        src={detail.fichier_url} 
                        alt="Aperçu" 
                        style={{ width: "100%", height: "auto", display: "block" }}
                      />
                    </div>
                  )}
                  
                  <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      onClick={() => handleForceDownload(detail.fichier_url, getFileName(detail.fichier_url))}
                      style={{ 
                        background: "#4aa3c7", 
                        color: "white", 
                        padding: "8px 16px", 
                        borderRadius: "8px", 
                        border: "none",
                        cursor: "pointer",
                        display: "inline-flex", 
                        alignItems: "center", 
                        gap: "8px",
                        fontSize: "13px",
                        fontWeight: "500"
                      }}
                    >
                      <i className="fa-solid fa-download" />
                      <span>Télécharger</span>
                    </button>
                    
                    <a 
                      href={detail.fichier_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        padding: "8px 16px", 
                        borderRadius: "8px", 
                        textDecoration: "none", 
                        display: "inline-flex", 
                        alignItems: "center", 
                        gap: "8px", 
                        background: "#F1F5F9", 
                        color: "#4aa3c7",
                        fontSize: "13px",
                        fontWeight: "500"
                      }}
                    >
                      <i className="fa-solid fa-eye" />
                      <span>Voir dans le navigateur</span>
                    </a>
                  </div>
                </div>
              )}

              {detail.destinataires && detail.destinataires.length > 0 && (
                <RecipientList 
                  destinataires={detail.destinataires} 
                  title="Liste des destinataires"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal de confirmation pour suppression ───────────────────────────────────
function ConfirmActionModal({ title, message, onConfirm, onClose }) {
  return (
    <div className="mm-overlay" onClick={onClose}>
      <div className="mm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "400px", textAlign: "center" }}>
        <div className="mm-modal-header" style={{ justifyContent: "center", borderBottom: "none", paddingBottom: "0" }}>
          <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", marginTop: "24px" }}>
            <i className="fa-solid fa-trash-can" style={{ color: "#EF4444", fontSize: "28px" }} />
          </div>
        </div>
        <div className="mm-modal-body" style={{ textAlign: "center", padding: "0 24px 24px 24px" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1E293B", marginBottom: "8px" }}>{title}</h3>
          <p style={{ fontSize: "14px", color: "#64748B", marginBottom: "24px" }}>{message}</p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <button className="ce-btn-ghost" onClick={onClose} style={{ padding: "8px 16px" }}>Annuler</button>
            <button className="mm-btn-ok" onClick={onConfirm} style={{ background: "#EF4444", color: "white", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer" }}>
              <i className="fa-solid fa-trash-can" /> Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Table destinataires ─────────────────
function RecipientsModal({ destinataires, onClose, onSave, savedSelections, groupe }) {
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  
  const [selected, setSelected] = useState(() => {
    if (savedSelections && savedSelections.length > 0) {
      return destinataires.map((_, i) => i).filter(idx => 
        savedSelections.some(s => s.email === destinataires[idx].email)
      );
    }
    return destinataires.map((_, i) => i);
  });

  useEffect(() => {
    const selectedContacts = selected.map((i) => destinataires[i]);
    onSave(selectedContacts);
  }, [selected, destinataires, onSave]);

  const filtered = destinataires.filter((d) => {
    const q = search.toLowerCase();
    const matchSearch = !q || 
      (d.nom || "").toLowerCase().includes(q) || 
      (d.email || "").toLowerCase().includes(q) || 
      (d.ville || "").toLowerCase().includes(q) ||
      (d.formation || "").toLowerCase().includes(q);
    
    if (groupe !== "Prospects") {
      const matchExtra = !q || 
        (d.session || "").toLowerCase().includes(q) ||
        (d.niveau || "").toLowerCase().includes(q);
      if (!matchExtra) return false;
    }
    
    let matchDate = true;
    if (dateFilter) {
      const dateValue = d.date_inscription || d.date_attestation;
      if (dateValue && dateValue !== "—") {
        const filterDate = dateFilter;
        const itemDate = dateValue.split('/').reverse().join('-');
        matchDate = filterDate === itemDate;
      } else {
        matchDate = false;
      }
    }
    
    return matchSearch && matchDate;
  });

  const allChecked = filtered.length > 0 && filtered.every((d) => selected.includes(destinataires.indexOf(d)));
  
  const toggleAll = () => {
    if (allChecked) {
      setSelected(selected.filter((i) => !filtered.map((d) => destinataires.indexOf(d)).includes(i)));
    } else {
      const filteredIdxs = filtered.map((d) => destinataires.indexOf(d));
      setSelected([...new Set([...selected, ...filteredIdxs])]);
    }
  };
  
  const toggle = (idx) => setSelected((prev) => prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx]);

  const getStatutBadgeStyle = (statut) => {
    if (!statut || statut === "—") return { background: "#F1F5F9", color: "#64748B" };
    if (statut.includes("Très intéressé") || statut.includes("🔥")) return { background: "#FEF3C7", color: "#D97706" };
    if (statut.includes("Intéressé moyen") || statut.includes("📈")) return { background: "#E0F2FE", color: "#0284C7" };
    if (statut.includes("Pas intéressé") || statut.includes("👀")) return { background: "#F1F5F9", color: "#64748B" };
    if (statut.includes("Perdu") || statut.includes("❄️")) return { background: "#FEF2F2", color: "#EF4444" };
    if (statut.includes("Étudiant")) return { background: "#D1FAE5", color: "#059669" };
    if (statut.includes("Attesté") || statut.includes("Diplômé")) return { background: "#E0E7FF", color: "#4338CA" };
    return { background: "#F0FDF4", color: "#16a34a" };
  };

  const showSessionNiveau = groupe !== "Prospects";

  return (
    <div className="mm-overlay" onClick={onClose}>
      <div className="mm-modal mm-recipients-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mm-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "#e8f5fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="fa-solid fa-users" style={{ color: "#4aa3c7", fontSize: "16px" }} />
            </div>
            <div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#1E293B" }}>Liste des destinataires</div>
              <div className="mm-recipients-counter">
                <i className="fa-solid fa-circle-check" style={{ color: "#4aa3c7" }} />
                Nombre de destinataires : <strong>{selected.length}</strong> / {destinataires.length}
              </div>
            </div>
          </div>
          <button className="mm-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>

        <div style={{ padding: "12px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <div className="mm-search" style={{ flex: 2, minWidth: "200px" }}>
            <i className="fa-solid fa-magnifying-glass mm-search-ic" />
            <input 
              className="mm-search-input" 
              placeholder={groupe === "Prospects" 
                ? "Rechercher par nom, email, ville ou formation..." 
                : "Rechercher par nom, email, ville, formation, session ou niveau..."}
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
            />
          </div>
          <DateSimpleFilter dateValue={dateFilter} setDateValue={setDateFilter} label="Date" />
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#94A3B8" }}>
              <i className="fa-regular fa-folder-open" style={{ fontSize: "28px", display: "block", marginBottom: "8px" }} />
              Aucun contact trouvé
              {search && <div style={{ fontSize: "12px", marginTop: "8px" }}>Essayez d'autres critères de recherche</div>}
            </div>
          ) : (
            <table className="mm-table">
              <thead>
                <tr>
                  <th className="mm-th-cb">
                    <span className={`mm-cb ${allChecked ? "mm-cb-on" : ""}`} onClick={toggleAll}>
                      {allChecked && <i className="fa-solid fa-check" />}
                    </span>
                  </th>
                  <th>Nom</th>
                  <th>Ville</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Formation(s)</th>
                  {showSessionNiveau && <th>Session(s)</th>}
                  {showSessionNiveau && <th>Niveau</th>}
                  <th>Statut</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const idx = destinataires.indexOf(d);
                  const niveauStyle = getNiveauColor(d.niveau);
                  // Affichage du statut avec "Attesté" si le statut est "Diplômé"
                  const displayStatut = d.statut === "Diplômé" ? "Attesté" : d.statut;
                  return (
                    <tr key={idx} className={selected.includes(idx) ? "mm-tr-sel" : ""}>
                      <td className="mm-td-cb">
                        <span className={`mm-cb ${selected.includes(idx) ? "mm-cb-on" : ""}`} onClick={() => toggle(idx)}>
                          {selected.includes(idx) && <i className="fa-solid fa-check" />}
                        </span>
                      </td>
                      <td style={{ fontWeight: "500", color: "#1E293B" }}>{d.nom || "—"}</td>
                      <td className="mm-td-gray">{d.ville || "—"}</td>
                      <td className="mm-td-gray" style={{ fontSize: "12px" }}>{d.email}</td>
                      <td className="mm-td-gray">{d.telephone || "—"}</td>
                      <td className="mm-td-gray" style={{ fontSize: "12px", maxWidth: "200px" }}>{d.formation || "—"}</td>
                      {showSessionNiveau && (
                        <td className="mm-td-gray" style={{ fontSize: "12px", maxWidth: "150px" }}>{d.session || "—"}</td>
                      )}
                      {showSessionNiveau && (
                        <td className="mm-td-gray">
                          {d.niveau && d.niveau !== "—" ? (
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "11px",
                              fontWeight: "500",
                              ...niveauStyle
                            }}>
                              {d.niveau}
                            </span>
                          ) : "—"}
                        </td>
                      )}
                      <td>
                        {d.statut && d.statut !== "—" && (
                          <span className="mm-badge" style={getStatutBadgeStyle(d.statut)}>
                            {displayStatut}
                          </span>
                        )}
                      </td>
                      <td className="mm-td-gray">{d.date_inscription || d.date_attestation || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: "14px 20px", borderTop: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "13px", color: "#64748B" }}>{selected.length} contact(s) sélectionné(s) sur {destinataires.length}</span>
          <div style={{ display: "flex", gap: "10px" }}>
            <button className="ce-btn-ghost" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════════
export default function MarketingMail() {
  const currentUser = authService.getCurrentUser();
  const userNom = currentUser ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() || currentUser.username || "Utilisateur" : "Utilisateur";
  const userEmail = currentUser?.email || "";
  const userInitial = userNom.charAt(0).toUpperCase();

  const [view, setView] = useState("liste");
  const [activeTab, setActiveTab] = useState("tous");
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState([]);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  const [detailEmailId, setDetailEmailId] = useState(null);

  const [form, setForm] = useState({
    send_mode: "segment",
    email_direct: "",
    groupe: "",
    formations_cibles: [],
    sessions_cibles: [],
    niveaux_cibles: [],
    statuts_prospects: [],
    objet: "",
    apercu: "",
    message: "",
    fichier: null,
  });

  const [isProspectOnly, setIsProspectOnly] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  
  const [formationsDisponibles, setFormationsDisponibles] = useState([]);
  const [sessionsDisponibles, setSessionsDisponibles] = useState([]);
  const [niveauxDisponibles, setNiveauxDisponibles] = useState([]);
  const [statutsAvecComptages, setStatutsAvecComptages] = useState(STATUTS_PROSPECT_COMPLETS);
  const [loadingFormations, setLoadingFormations] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingNiveaux, setLoadingNiveaux] = useState(false);

  const [loadingApercu, setLoadingApercu] = useState(false);
  const [loadingBody, setLoadingBody] = useState(false);
  const [previewSugs, setPreviewSugs] = useState([]);

  const [showRecipients, setShowRecipients] = useState(false);
  const [recipientsList, setRecipientsList] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [savedSelectedRecipients, setSavedSelectedRecipients] = useState([]);

  // ─── Fonctions API ──────────────────────────────────────────────────────────
  const fetchSessionsByFormations = useCallback(async (formationIds, type = "etudiants") => {
    if (!formationIds || formationIds.length === 0) return [];
    try {
      let url = '/marketing-mail/sessions/';
      if (type === "diplomes") {
        url = '/marketing-mail/sessions/diplomes/';
      }
      const response = await api.get(url, { 
        params: { formation_ids: formationIds.join(',') } 
      });
      return response.data;
    } catch (err) {
      console.error("Erreur chargement sessions:", err);
      return [];
    }
  }, []);

  // ─── Cache functions ────────────────────────────────────────────────────────
  const loadStatutsFromCache = useCallback((formationsKey) => {
    try {
      const cached = localStorage.getItem(STATUTS_CACHE_KEY);
      if (cached) {
        const { data, timestamp, formationsKey: cachedKey } = JSON.parse(cached);
        if (cachedKey === formationsKey && (Date.now() - timestamp) < CACHE_DURATION) {
          setStatutsAvecComptages(data);
          return true;
        }
      }
    } catch (e) {
      console.error("Erreur lecture cache:", e);
    }
    return false;
  }, []);

  const saveStatutsToCache = useCallback((data, formationsKey) => {
    try {
      localStorage.setItem(STATUTS_CACHE_KEY, JSON.stringify({
        data: data,
        formationsKey: formationsKey,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error("Erreur sauvegarde cache:", e);
    }
  }, []);

  // ── Chargement des statuts ─────────────────────────────────────────────────
  const loadStatutsWithCounts = useCallback(async () => {
    if (!isProspectOnly && form.groupe !== "Prospects") {
      setStatutsAvecComptages(STATUTS_PROSPECT_COMPLETS);
      return;
    }

    const formationsKey = JSON.stringify(form.formations_cibles || []);
    
    if (loadStatutsFromCache(formationsKey)) {
      return;
    }

    setStatutsAvecComptages(STATUTS_PROSPECT_COMPLETS);
    
    setTimeout(async () => {
      try {
        const statutsData = await fetchStatutsDisponibles("prospects", form.formations_cibles || []);
        const countsMap = {};
        statutsData.forEach(item => {
          countsMap[item.value] = item.nombre;
        });
        const updatedStatuts = STATUTS_PROSPECT_COMPLETS.map(s => ({
          ...s,
          nombre: countsMap[s.value] || 0
        }));
        setStatutsAvecComptages(updatedStatuts);
        saveStatutsToCache(updatedStatuts, formationsKey);
      } catch (err) {
        console.error("Erreur chargement statuts:", err);
      }
    }, 50);
  }, [form.groupe, form.formations_cibles, isProspectOnly, loadStatutsFromCache, saveStatutsToCache]);

  // ── Chargement des sessions ────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    const formationsIds = form.formations_cibles || [];
    const isDiplomes = form.groupe === "Diplômés";
    
    if ((form.groupe !== "Étudiants" && !isDiplomes) || formationsIds.length === 0) {
      setSessionsDisponibles([]);
      return;
    }

    setLoadingSessions(true);
    try {
      const type = isDiplomes ? "diplomes" : "etudiants";
      const sessions = await fetchSessionsByFormations(formationsIds, type);
      setSessionsDisponibles(sessions);
      
      const existingSessionIds = sessions.map(s => s.id);
      const newSelectedSessions = form.sessions_cibles.filter(id => existingSessionIds.includes(id));
      if (newSelectedSessions.length !== form.sessions_cibles.length) {
        setForm(prev => ({ ...prev, sessions_cibles: newSelectedSessions }));
      }
    } catch (err) {
      console.error("Erreur:", err);
      setSessionsDisponibles([]);
    } finally {
      setLoadingSessions(false);
    }
  }, [form.groupe, form.formations_cibles, form.sessions_cibles, fetchSessionsByFormations]);

  // ── Chargement des niveaux ─────────────────────────────────────────────────
  const loadNiveaux = useCallback(async () => {
    const formationsIds = form.formations_cibles || [];
    const isDiplomes = form.groupe === "Diplômés";
    
    if ((form.groupe !== "Étudiants" && !isDiplomes) || formationsIds.length === 0) {
      setNiveauxDisponibles([]);
      return;
    }

    setLoadingNiveaux(true);
    try {
      const type = isDiplomes ? "diplomes" : "etudiants";
      const sessions = await fetchSessionsByFormations(formationsIds, type);
      
      const niveauxUniques = [...new Set(sessions.map(s => s.niveau).filter(n => n))];
      
      const niveauxList = niveauxUniques.map(niveau => {
        let label = niveau;
        let order = 3;
        if (niveau === "Débutant") { label = "Débutant"; order = 1; }
        else if (niveau === "Intermédiaire") { label = "Intermédiaire"; order = 2; }
        else if (niveau === "Avancé") { label = "Avancé"; order = 3; }
        return { value: niveau, label: label, order: order };
      });
      
      niveauxList.sort((a, b) => a.order - b.order);
      
      setNiveauxDisponibles(niveauxList);
      
      const existingNiveaux = niveauxList.map(n => n.value);
      const newSelectedNiveaux = form.niveaux_cibles.filter(n => existingNiveaux.includes(n));
      if (newSelectedNiveaux.length !== form.niveaux_cibles.length) {
        setForm(prev => ({ ...prev, niveaux_cibles: newSelectedNiveaux }));
      }
    } catch (err) {
      console.error("Erreur chargement niveaux:", err);
      setNiveauxDisponibles([]);
    } finally {
      setLoadingNiveaux(false);
    }
  }, [form.groupe, form.formations_cibles, form.niveaux_cibles, fetchSessionsByFormations]);

  // ── Génération IA ──────────────────────────────────────────────────────────
  const generateApercu = async () => {
    if (!form.objet || form.objet.trim() === "") {
      setError("Veuillez d'abord remplir l'objet pour générer l'aperçu.");
      return;
    }
    setError("");
    setLoadingApercu(true);
    setPreviewSugs([]);
    try {
      const groupeActuel = isProspectOnly ? "Prospects" : form.groupe;
      let payload;

      if (form.send_mode === "segment" && groupeActuel) {
        const cibleLabel =
          groupeActuel === "Prospects" ? "prospect" :
          groupeActuel === "Étudiants" ? "étudiant" : "diplômé";
        const formationNom = formationsDisponibles
          .filter(f => form.formations_cibles.includes(f.id))
          .map(f => f.intitule)
          .join(", ") || "formation professionnelle";

        const sessionNom = sessionsDisponibles
          .filter(s => form.sessions_cibles.includes(s.id))
          .map(s => s.intitule)
          .join(", ") || "";

        const niveauNom = form.niveaux_cibles.join(", ") || "";

        payload = {
          mode: "segment",
          cible: cibleLabel,
          formation: formationNom,
          objet: form.objet,
          session: sessionNom,
          niveau: niveauNom,
        };
      } else {
        payload = {
          mode: "individual",
          objet: form.objet,
        };
      }

      const data = await generatePreviewAI(payload);
      const suggestions = data?.preview || [];
      if (suggestions.length > 0) {
        setPreviewSugs(suggestions);
      } else {
        setError("L'IA n'a pas pu générer de proposition. Vérifiez l'objet saisi.");
      }
    } catch (err) {
      setError("Erreur lors de la génération de l'aperçu. Réessayez.");
    } finally {
      setLoadingApercu(false);
    }
  };

  const generateMessage = async () => {
    if (!form.objet || form.objet.trim() === "") {
      setError("Veuillez d'abord remplir l'objet pour générer le message.");
      return;
    }
    if (!form.apercu || form.apercu.trim() === "") {
      setError("Veuillez d'abord remplir le texte d'aperçu pour générer le message.");
      return;
    }
    setError("");
    setLoadingBody(true);
    try {
      const groupeActuel = isProspectOnly ? "Prospects" : form.groupe;
      let payload;

      if (form.send_mode === "segment" && groupeActuel) {
        const cibleLabel =
          groupeActuel === "Prospects" ? "prospect" :
          groupeActuel === "Étudiants" ? "étudiant" : "diplômé";
        const formationNom = formationsDisponibles
          .filter(f => form.formations_cibles.includes(f.id))
          .map(f => f.intitule)
          .join(", ") || "formation professionnelle";

        const sessionNom = sessionsDisponibles
          .filter(s => form.sessions_cibles.includes(s.id))
          .map(s => s.intitule)
          .join(", ") || "";

        const niveauNom = form.niveaux_cibles.join(", ") || "";

        payload = {
          mode: "segment",
          cible: cibleLabel,
          formation: formationNom,
          objet: form.objet,
          preview: form.apercu,
          session: sessionNom,
          niveau: niveauNom,
        };
      } else {
        payload = {
          mode: "individual",
          objet: form.objet,
          preview: form.apercu,
        };
      }

      const data = await generateBodyAI(payload);
      const bodyText = data?.body || "";
      if (bodyText) {
        setForm(prev => ({ ...prev, message: bodyText }));
      } else {
        setError("L'IA n'a pas pu générer le corps. Vérifiez l'objet et l'aperçu.");
      }
    } catch (err) {
      setError("Erreur lors de la génération du corps. Réessayez.");
    } finally {
      setLoadingBody(false);
    }
  };

  // ── Chargement des formations ──────────────────────────────────────────────
  const fetchFormationsByType = useCallback(async (groupe) => {
    if (!groupe) {
      setFormationsDisponibles([]);
      return;
    }
    
    setLoadingFormations(true);
    try {
      let type = "";
      if (groupe === "Prospects") type = "prospects";
      else if (groupe === "Étudiants") type = "etudiants";
      else if (groupe === "Diplômés") type = "diplomes";
      
      if (type) {
        const formationsData = await fetchFormationsParType(type);
        setFormationsDisponibles(formationsData);
        
        const existingIds = formationsData.map(f => f.id);
        const newFormationsCibles = form.formations_cibles.filter(id => existingIds.includes(id));
        if (newFormationsCibles.length !== form.formations_cibles.length) {
          setForm(prev => ({ ...prev, formations_cibles: newFormationsCibles }));
        }
      } else {
        setFormationsDisponibles([]);
      }
    } catch (err) {
      console.error("Erreur chargement formations:", err);
      setFormationsDisponibles([]);
    } finally {
      setLoadingFormations(false);
    }
  }, [form.formations_cibles]);

  // ── Chargement emails ──────────────────────────────────────────────────────
  const chargerEmails = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const isArchive = activeTab === "archive";
      const isDirect = activeTab === "direct";
      let groupe = "";
      if (["Prospects", "Étudiants", "Diplômés"].includes(activeTab)) {
        groupe = activeTab;
      }
      const params = { archive: isArchive, groupe, search: searchText, direct: isDirect };
      
      if (dateFilter && dateFilter.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dateFilter)) {
        params.date_unique = dateFilter;
      }
      
      const data = await fetchEmails(params);
      setEmails(data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Erreur chargement emails:", err);
      setError("Impossible de charger les emails.");
      setEmails([]);
    } finally { setLoading(false); }
  }, [activeTab, searchText, dateFilter]);

  // ── Effets ─────────────────────────────────────────────────────────────────
  useEffect(() => { chargerEmails(); }, [chargerEmails]);
  useEffect(() => { loadStatutsWithCounts(); }, [loadStatutsWithCounts]);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadNiveaux(); }, [loadNiveaux]);

  useEffect(() => {
    if (form.groupe && !isProspectOnly) {
      fetchFormationsByType(form.groupe);
    } else if (isProspectOnly) {
      fetchFormationsByType("Prospects");
    } else {
      setFormationsDisponibles([]);
    }
  }, [form.groupe, isProspectOnly, fetchFormationsByType]);

  // ── Chargement automatique des destinataires ───────────────────────────────
  useEffect(() => {
    if (form.send_mode === "segment" && (form.groupe || isProspectOnly)) {
      const chargerDestinatairesAuto = async () => {
        const groupeActuel = isProspectOnly ? "Prospects" : form.groupe;
        if (!groupeActuel) return;
        
        try {
          const formationsIds = form.formations_cibles.map((id) => Number(id));
          const sessionsIds = form.sessions_cibles.map((id) => Number(id));
          const niveaux = form.niveaux_cibles || [];
          
          const payload = {
            groupe: groupeActuel,
            formations_cibles: formationsIds,
            sessions_cibles: sessionsIds,
            statuts_prospects: (groupeActuel === "Étudiants" || groupeActuel === "Diplômés") ? niveaux : form.statuts_prospects,
            sources_prospects: [],
          };
          const res = await estimerDestinataires(payload);
          if (res && res.destinataires) {
            setRecipientsList(res.destinataires);
            setSavedSelectedRecipients(res.destinataires);
          } else {
            setSavedSelectedRecipients([]);
          }
        } catch (err) {
          console.error("Erreur chargement destinataires:", err);
          setSavedSelectedRecipients([]);
        }
      };
      
      chargerDestinatairesAuto();
    }
  }, [form.send_mode, form.groupe, isProspectOnly, form.formations_cibles, form.sessions_cibles, form.niveaux_cibles, form.statuts_prospects]);

  // ── Pagination ──
  const totalPages = Math.ceil(emails.length / itemsPerPage);
  const paginatedEmails = emails.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const allSelected = paginatedEmails.length > 0 && paginatedEmails.every((e) => selected.includes(e.id));

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    setSelected([]);
    setCurrentPage(1);
    setSearchText("");
    setDateFilter("");
  };

  const handleArchive = async () => {
    try {
      await archiverEmails(selected);
      setSelected([]);
      setShowArchiveModal(false);
      chargerEmails();
    } catch {
      setError("Erreur lors de l'archivage.");
    }
  };

  const handleDelete = async () => {
    try {
      await supprimerEmails(selected);
      setSelected([]);
      setShowDeleteModal(false);
      chargerEmails();
    } catch {
      setError("Erreur lors de la suppression.");
    }
  };

  const voirDestinataires = async () => {
    const groupeActuel = isProspectOnly ? "Prospects" : form.groupe;
    
    if (!groupeActuel) {
      setError("Veuillez d'abord sélectionner un public.");
      return;
    }
    
    setLoadingRecipients(true);
    setError("");
    try {
      const formationsIds = form.formations_cibles.map((id) => Number(id));
      const sessionsIds = form.sessions_cibles.map((id) => Number(id));
      const niveaux = form.niveaux_cibles || [];
      
      const payload = {
        groupe: groupeActuel,
        formations_cibles: formationsIds,
        sessions_cibles: sessionsIds,
        statuts_prospects: (groupeActuel === "Étudiants" || groupeActuel === "Diplômés") ? niveaux : form.statuts_prospects,
        sources_prospects: [],
      };
      
      const res = await estimerDestinataires(payload);
      
      if (res && res.destinataires) {
        setRecipientsList(res.destinataires);
      } else {
        setRecipientsList([]);
        setError("Aucun destinataire trouvé pour ces critères.");
      }
      setShowRecipients(true);
    } catch (err) {
      console.error("Erreur:", err);
      setError("Erreur lors du chargement des destinataires.");
      setRecipientsList([]);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleSaveRecipientsSelection = (selectedContacts) => {
    setSavedSelectedRecipients(selectedContacts);
    setError("");
  };

  const handleEnvoyer = async () => {
    if (!form.objet.trim()) { setError("L'objet est obligatoire."); return; }
    if (!form.message.trim()) { setError("Le message est obligatoire."); return; }
    if (form.send_mode === "direct" && !form.email_direct.trim()) { setError("L'adresse email est obligatoire."); return; }
    
    if (form.send_mode === "segment" && savedSelectedRecipients.length === 0) {
      setError("Aucun destinataire trouvé pour ces critères.");
      return;
    }

    setSending(true);
    setError("");
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("send_mode", form.send_mode);
      formDataToSend.append("objet", form.objet);
      formDataToSend.append("apercu", form.apercu || "");
      formDataToSend.append("message", form.message);
      
      if (form.send_mode === "direct") {
        formDataToSend.append("email_direct", form.email_direct);
      } else {
        const groupeActuel = isProspectOnly ? "Prospects" : form.groupe;
        formDataToSend.append("groupe", groupeActuel);
        
        const selectedEmails = savedSelectedRecipients.map(r => r.email);
        formDataToSend.append("emails_selected", JSON.stringify(selectedEmails));
        
        formDataToSend.append("formations_cibles", JSON.stringify(form.formations_cibles.map((id) => Number(id))));
        formDataToSend.append("sessions_cibles", JSON.stringify(form.sessions_cibles.map((id) => Number(id))));
        formDataToSend.append("niveaux_cibles", JSON.stringify(form.niveaux_cibles || []));
        formDataToSend.append("statuts_prospects", JSON.stringify(form.statuts_prospects || []));
        formDataToSend.append("sources_prospects", JSON.stringify([]));
      }
      if (form.fichier) formDataToSend.append("fichier", form.fichier);

      await envoyerEmail(formDataToSend);
      
      setForm({
        send_mode: "segment",
        email_direct: "",
        groupe: "",
        formations_cibles: [],
        sessions_cibles: [],
        niveaux_cibles: [],
        statuts_prospects: [],
        objet: "",
        apercu: "",
        message: "",
        fichier: null,
      });
      setIsProspectOnly(false);
      setSavedSelectedRecipients([]);
      setShowRecipients(false);
      setActiveTab("tous");
      setSearchText("");
      setDateFilter("");
      setCurrentPage(1);
      setSelected([]);
      
      await chargerEmails();
      setView("liste");
    } catch (err) {
      if (err?.response?.data) {
        const data = err.response.data;
        if (typeof data === "object") {
          const firstError = Object.values(data)[0];
          setError(Array.isArray(firstError) ? firstError[0] : String(firstError));
        } else setError(String(data));
      } else setError(err?.message || "Erreur lors de l'envoi.");
    } finally { setSending(false); }
  };

  const groupeColor = (g) => {
    if (g === "Prospects") return "#f59e0b";
    if (g === "Étudiants") return "#22c55e";
    if (g === "Diplômés") return "#6366f1";
    return "#94A3B8";
  };

  // Fonction pour afficher le libellé du groupe (Attestés au lieu de Diplômés)
  const getGroupeDisplay = (g) => {
    if (g === "Diplômés") return "Attestés";
    return g;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  //  VUE TEMPLATES
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "templates") {
    return (
      <Layout>
        <div className="mm-page">
          <div className="mm-header">
            <div>
              <button className="ce-back-btn" onClick={() => { setView("liste"); setError(""); }}>
                <i className="fa-solid fa-arrow-left" /> Retour
              </button>
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1E293B", marginBottom: "6px" }}>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ color: "#4aa3c7", marginRight: "10px" }} />
              Choisir un modèle
            </h2>
            <p style={{ fontSize: "13px", color: "#94A3B8" }}>Sélectionnez un point de départ pour votre campagne email</p>
          </div>

          <div className="mm-templates-grid">
            {EMAIL_TEMPLATES.map((tpl) => (
              <div
                key={tpl.id}
                className="mm-template-card"
                onClick={() => {
                  const newFormState = {
                    ...form,
                    objet: tpl.objet,
                    apercu: tpl.apercu,
                    message: tpl.message,
                    formations_cibles: [],
                    sessions_cibles: [],
                    niveaux_cibles: [],
                    statuts_prospects: [],
                  };
                  
                  if (tpl.groupeFiltre) {
                    newFormState.groupe = tpl.groupeFiltre;
                    newFormState.send_mode = "segment";
                  }
                  
                  setIsProspectOnly(tpl.isProspectOnly || false);
                  setSelectedTemplateId(tpl.id);
                  setForm(newFormState);
                  setView("creer");
                  setError("");
                }}
              >
                <div className="mm-template-icon" style={{ background: tpl.bgColor, color: tpl.color }}>
                  <i className={tpl.icon} />
                </div>
                <div className="mm-template-title">{tpl.title}</div>
                <div className="mm-template-desc">{tpl.description}</div>
                {tpl.groupeFiltre && (
                  <div className="mm-template-cta" style={{ fontSize: "10px", color: "#f59e0b" }}>
                    <i className="fa-solid fa-bullseye" /> Cible: {tpl.groupeFiltre}
                  </div>
                )}
                <div className="mm-template-cta">
                  Utiliser ce modèle <i className="fa-solid fa-arrow-right" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  VUE CRÉER
  // ═══════════════════════════════════════════════════════════════════════════
  if (view === "creer") {
    const isEmailVide = selectedTemplateId === "vide";
    const isEtudiantOuDiplome = form.groupe === "Étudiants" || form.groupe === "Diplômés";

    const hasObjetContent = form.objet && form.objet.trim() !== "";
    const generateApercuButtonClass = hasObjetContent ? "ce-generate-btn ce-generate-btn-active" : "ce-generate-btn";
    
    const hasObjetAndApercuContent = (form.objet && form.objet.trim() !== "") && (form.apercu && form.apercu.trim() !== "");
    const generateMessageButtonClass = hasObjetAndApercuContent ? "ce-generate-btn ce-generate-btn-active" : "ce-generate-btn";

    const hasFormationSelected = form.formations_cibles && form.formations_cibles.length > 0;

    // Affichage du label de formation pour Attestés
    const getFormationLabel = () => {
      if (isProspectOnly) return "Formation souhaitée";
      if (form.groupe === "Prospects") return "Formation souhaitée";
      if (form.groupe === "Étudiants") return "Formation suivie";
      if (form.groupe === "Diplômés") return "Attestation obtenue";
      return "Formation";
    };

    return (
      <Layout>
        <div className="ce-page">
          <div className="ce-topbar">
            <button className="ce-back-btn" onClick={() => { setView("templates"); setError(""); setIsProspectOnly(false); setSavedSelectedRecipients([]); setSelectedTemplateId(""); setPreviewSugs([]); }}>
              <i className="fa-solid fa-arrow-left" /> Changer de modèle
            </button>
            <h2 className="ce-topbar-title">Nouvel e-mail</h2>
          </div>

          {error && (
            <div className="mm-error-banner">
              <i className="fa-solid fa-circle-exclamation" /> {error}
            </div>
          )}

          {form.send_mode === "segment" && savedSelectedRecipients.length > 0 && (
            <div className="mm-info-banner" style={{ marginBottom: "12px", padding: "10px 16px", background: "#e8f5fb", borderRadius: "8px", fontSize: "13px", color: "#4aa3c7" }}>
              <i className="fa-solid fa-users" /> {savedSelectedRecipients.length} destinataire(s) trouvé(s) pour ces critères
            </div>
          )}

          <div className="ce-body">
            <div className="ce-panel">
              <div className="ce-field">
                <label className="ce-lbl">De</label>
                <div className="ce-from-box">
                  <div className="ce-avatar-sm">{userInitial}</div>
                  <div>
                    <div className="ce-from-name">{userNom}</div>
                    <div className="ce-from-mail">{userEmail}</div>
                  </div>
                </div>
              </div>

              <div className="ce-field">
                <label className="ce-lbl">Méthode d'envoi <span className="ce-req">*</span></label>
                <select
                  className="ce-select"
                  value={form.send_mode}
                  onChange={(e) => {
                    setForm({
                      ...form,
                      send_mode: e.target.value,
                      email_direct: "",
                      groupe: "",
                      formations_cibles: [],
                      sessions_cibles: [],
                      niveaux_cibles: [],
                      statuts_prospects: []
                    });
                    setSavedSelectedRecipients([]);
                  }}
                >
                  <option value="segment">À un segment de contacts</option>
                  <option value="direct">À une adresse e-mail directe</option>
                </select>
              </div>

              {form.send_mode === "direct" && (
                <div className="ce-field">
                  <label className="ce-lbl">Adresse e-mail <span className="ce-req">*</span></label>
                  <input className="ce-input" type="email" placeholder="exemple@email.com" value={form.email_direct} onChange={(e) => setForm({ ...form, email_direct: e.target.value })} />
                </div>
              )}

              {form.send_mode === "segment" && (
                <div className="ce-field">
                  <label className="ce-lbl">Type de contact <span className="ce-req">*</span></label>
                  
                  {isProspectOnly ? (
                    <div className="ce-public-grid">
                      <label className="ce-public-card ce-public-on" style={{ borderColor: "#f59e0b", background: "#fffbeb" }}>
                        <input type="radio" name="pub" checked={true} readOnly />
                        <i className="fa-solid fa-user-plus ce-pub-icon" style={{ color: "#f59e0b" }} />
                        <span>Prospects</span>
                      </label>
                    </div>
                  ) : (
                    <div className="ce-public-grid">
                      {["Prospects", "Étudiants", "Diplômés"].map((g) => (
                        <label key={g} className={`ce-public-card ${form.groupe === g ? "ce-public-on" : ""}`}>
                          <input
                            type="radio"
                            name="pub"
                            checked={form.groupe === g}
                            onChange={() => {
                              setForm({
                                ...form,
                                groupe: g,
                                formations_cibles: [],
                                sessions_cibles: [],
                                niveaux_cibles: [],
                                statuts_prospects: []
                              });
                              setSavedSelectedRecipients([]);
                            }}
                          />
                          <i className={`fa-solid ${g === "Prospects" ? "fa-user-plus" : g === "Étudiants" ? "fa-user-graduate" : "fa-award"} ce-pub-icon`} style={{ color: groupeColor(g) }} />
                          <span>{g === "Diplômés" ? "Attestés" : g}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {(form.groupe || isProspectOnly) && (
                    <div className="ce-subfilters">
                      <p className="ce-sub-title">
                        <i className="fa-solid fa-sliders" /> Affiner le public
                      </p>

                      <ChipGroupFormations
                        items={formationsDisponibles}
                        selected={form.formations_cibles}
                        onChange={(v) => {
                          setForm({
                            ...form,
                            formations_cibles: v,
                            sessions_cibles: [],
                            niveaux_cibles: []
                          });
                          setSavedSelectedRecipients([]);
                        }}
                        title={getFormationLabel()}
                        loading={loadingFormations}
                      />

                      {isEtudiantOuDiplome && (
                        <>
                          <ChipGroupSessions
                            items={sessionsDisponibles}
                            selected={form.sessions_cibles}
                            onChange={(v) => {
                              setForm({
                                ...form,
                                sessions_cibles: v,
                              });
                              setSavedSelectedRecipients([]);
                            }}
                            title="Session de formation"
                            loading={loadingSessions}
                          />

                          <ChipGroupNiveaux
                            items={niveauxDisponibles}
                            selected={form.niveaux_cibles}
                            onChange={(v) => {
                              setForm({ ...form, niveaux_cibles: v });
                              setSavedSelectedRecipients([]);
                            }}
                            title="Niveau de session"
                            loading={loadingNiveaux}
                          />
                        </>
                      )}

                      {(form.groupe === "Prospects" || isProspectOnly) && (
                        <ChipGroupStatuts
                          items={statutsAvecComptages}
                          selected={form.statuts_prospects}
                          onChange={(v) => {
                            setForm({ ...form, statuts_prospects: v });
                            setSavedSelectedRecipients([]);
                          }}
                          title="Statut IA prospect"
                          formationSelected={hasFormationSelected}
                        />
                      )}

                      <button className="ce-voir-liste-btn" onClick={voirDestinataires} disabled={loadingRecipients}>
                        {loadingRecipients
                          ? <><i className="fa-solid fa-spinner fa-spin" /> Chargement...</>
                          : <><i className="fa-solid fa-users" /> Voir la liste des destinataires</>}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="ce-preview">
              <div className="ce-preview-header">
                <span className="ce-preview-title">Aperçu de l'e-mail</span>
              </div>

              <div className="ce-preview-meta-box">
                {[
                  ["Envoyer à", form.send_mode === "direct" ? (form.email_direct || "—") : (savedSelectedRecipients.length > 0 ? `${savedSelectedRecipients.length} contact(s)` : (isProspectOnly ? "Prospects" : (form.groupe === "Diplômés" ? "Attestés" : (form.groupe || "—"))))],
                  ["De", `${userNom} (${userEmail})`],
                ].map(([k, v]) => (
                  <div key={k} className="ce-meta-row">
                    <span className="ce-meta-key">{k} :</span>
                    <span className="ce-meta-val">{v}</span>
                  </div>
                ))}
              </div>

              <div className="ce-email-card" style={{ marginTop: "16px" }}>
                <div className="ce-email-top">
                  <div className="ce-email-brand">CRM</div>
                </div>
                <div style={{ padding: "16px 24px 0 24px", borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ fontSize: "11px", color: "#94A3B8", marginBottom: "4px" }}>Objet :</div>
                  <div style={{ fontSize: "16px", fontWeight: "600", color: "#1E293B", marginBottom: "8px" }}>{form.objet || "—"}</div>
                  {form.apercu && <div style={{ fontSize: "12px", color: "#64748B", marginBottom: "12px" }}>Aperçu : {form.apercu}</div>}
                </div>
                {form.message
                  ? <div className="ce-email-body" style={{ whiteSpace: "pre-wrap" }}>{form.message}</div>
                  : <div className="ce-email-drop"><i className="fa-regular fa-envelope" /><p>Écrivez votre message ici</p></div>}
                {form.fichier && (
                  <div className="ce-attach-preview">
                    <i className="fa-solid fa-paperclip" /> {form.fichier.name}
                  </div>
                )}
                <div className="ce-email-footer">
                  <button type="button" style={{ background: "none", border: "none", padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>Se désabonner</button>
                  {' · '}
                  <button type="button" style={{ background: "none", border: "none", padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>Gérer les préférences</button>
                </div>
              </div>

              <div style={{ margin: "16px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                  <label className="ce-lbl">Ligne d'objet <span className="ce-req">*</span></label>
                  <input className="ce-input" placeholder="Objet de votre e-mail..." value={form.objet} onChange={(e) => setForm({ ...form, objet: e.target.value })} />
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "7px" }}>
                    <label className="ce-lbl" style={{ marginBottom: 0 }}>Texte d'aperçu</label>
                    {isEmailVide && (
                      <button
                        className={generateApercuButtonClass}
                        onClick={generateApercu}
                        type="button"
                        disabled={loadingApercu}
                      >
                        {loadingApercu
                          ? <><i className="fa-solid fa-spinner fa-spin" /> Génération...</>
                          : <><i className="fa-solid fa-wand-magic-sparkles" /> Générer</>}
                      </button>
                    )}
                  </div>
                  <input
                    className="ce-input"
                    placeholder="Court texte visible dans la boîte de réception..."
                    value={form.apercu}
                    onChange={(e) => { setForm({ ...form, apercu: e.target.value }); setPreviewSugs([]); }}
                  />
                  {isEmailVide && previewSugs.length > 0 && (
                    <div style={{ marginTop: "8px" }}>
                      <div style={{ fontSize: "11px", color: "#64748B", marginBottom: "5px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                          <i className="fa-solid fa-lightbulb" style={{ color: "#f59e0b" }} /> Suggestions :
                        </span>
                        <button
                          type="button"
                          onClick={() => setPreviewSugs([])}
                          style={{ background: "none", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: "11px", display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          <i className="fa-solid fa-xmark" /> Fermer les suggestions
                        </button>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {previewSugs.map((s, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "8px 12px", fontSize: "12px", borderRadius: "7px",
                              border: "1px solid #B6E0F3", background: "#EBF7FD",
                              color: "#0E7490", display: "flex", alignItems: "center", gap: "8px",
                              lineHeight: "1.4", justifyContent: "space-between",
                            }}
                          >
                            <span style={{ flex: 1 }}>{s}</span>
                            <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setForm(prev => ({ ...prev, apercu: s }));
                                  setPreviewSugs([]);
                                }}
                                title="Insérer cette proposition"
                                style={{
                                  padding: "4px 8px", fontSize: "11px", borderRadius: "4px",
                                  border: "1px solid #4aa3c7", background: "#ffffff",
                                  color: "#4aa3c7", cursor: "pointer"
                                }}
                              >
                                <i className="fa-solid fa-arrow-right" style={{ marginRight: "4px" }} />
                                Insérer
                              </button>
                              <button
                                type="button"
                                onClick={generateApercu}
                                disabled={loadingApercu}
                                title="Générer d'autres propositions"
                                style={{
                                  padding: "4px 8px", fontSize: "11px", borderRadius: "4px",
                                  border: "1px solid #4aa3c7", background: "#4aa3c7",
                                  color: "#ffffff", cursor: "pointer", fontWeight: "bold"
                                }}
                              >
                                {loadingApercu ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-plus" />}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "7px" }}>
                    <label className="ce-lbl" style={{ marginBottom: 0 }}>Corps du message <span className="ce-req">*</span></label>
                    {isEmailVide && (
                      <button
                        className={generateMessageButtonClass}
                        onClick={generateMessage}
                        type="button"
                        disabled={loadingBody}
                      >
                        {loadingBody
                          ? <><i className="fa-solid fa-spinner fa-spin" /> Génération...</>
                          : <><i className="fa-solid fa-wand-magic-sparkles" /> Générer</>}
                      </button>
                    )}
                  </div>
                  <textarea
                    className="ce-textarea"
                    rows={8}
                    placeholder="Bonjour,Votre message ici..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>
                <div>
                  <label className="ce-lbl">Pièce jointe</label>
                  <label className="ce-file-lbl" style={{ display: "inline-flex", width: "auto" }}>
                    <input type="file" style={{ display: "none" }} onChange={(e) => setForm({ ...form, fichier: e.target.files[0] || null })} />
                    <i className="fa-solid fa-paperclip" /> {form.fichier ? form.fichier.name : "Joindre un fichier"}
                  </label>
                  {form.fichier && (
                    <button onClick={() => setForm({ ...form, fichier: null })} style={{ marginLeft: "10px", background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: "12px" }}>
                      <i className="fa-solid fa-xmark" /> Retirer
                    </button>
                  )}
                </div>
              </div>

              <div style={{ margin: "0 24px 24px 24px", display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #E2E8F0", paddingTop: "20px" }}>
                <button className="ce-btn-ghost" onClick={() => { setView("liste"); setError(""); setIsProspectOnly(false); setSavedSelectedRecipients([]); setSelectedTemplateId(""); setPreviewSugs([]); }}>Annuler</button>
                <button className="ce-btn-send" onClick={handleEnvoyer} disabled={sending || (form.send_mode === "segment" && savedSelectedRecipients.length === 0)}>
                  {sending
                    ? <><i className="fa-solid fa-spinner fa-spin" /> Envoi en cours...</>
                    : <><i className="fa-solid fa-paper-plane" /> Envoyer {form.send_mode === "segment" && savedSelectedRecipients.length > 0 ? `(${savedSelectedRecipients.length})` : ""}</>}
                </button>
              </div>
            </div>
          </div>

          {showRecipients && (
            <RecipientsModal
              destinataires={recipientsList}
              onClose={() => setShowRecipients(false)}
              onSave={handleSaveRecipientsSelection}
              savedSelections={savedSelectedRecipients}
              groupe={isProspectOnly ? "Prospects" : form.groupe}
            />
          )}
        </div>
      </Layout>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  VUE LISTE
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Layout>
      <div className="mm-page">
        <div className="mm-header">
          <div>
            <h1 className="mm-title">
              <i className="fa-solid fa-envelope mm-icon" /> Marketing Mail
            </h1>
            <p className="mm-sub">Gérez et envoyez vos campagnes email</p>
          </div>
          <button className="mm-create-btn" onClick={() => { setView("templates"); setError(""); }}>
            <i className="fa-solid fa-plus" /> Créer un e-mail
          </button>
        </div>

        {error && (
          <div className="mm-error-banner" style={{ marginBottom: "12px" }}>
            <i className="fa-solid fa-circle-exclamation" /> {error}
          </div>
        )}

        <div className="mm-card">
          <div className="mm-tabs">
            {TABS.map((tab) => (
              <button key={tab.id} className={`mm-tab ${activeTab === tab.id ? "mm-tab-on" : ""}`} onClick={() => switchTab(tab.id)}>
                {tab.id !== "tous" && tab.id !== "archive" && tab.id !== "direct" && <span className="mm-tab-dot" style={{ background: groupeColor(tab.id) }} />}
                {tab.id === "direct" && <i className="fa-solid fa-at" style={{ fontSize: 9, color: "#4aa3c7", marginRight: "4px" }} />}
                {tab.id === "archive" && <i className="fa-regular fa-circle" style={{ fontSize: 9, color: "#94A3B8" }} />}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mm-toolbar">
            <div className="mm-toolbar-l">
              <div className="mm-search">
                <i className="fa-solid fa-magnifying-glass mm-search-ic" />
                <input className="mm-search-input" placeholder="Rechercher par objet ou expéditeur..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
              </div>
              <DateFilter dateValue={dateFilter} setDateValue={setDateFilter} />
            </div>
            {selected.length > 0 && (
              <div style={{ display: "flex", gap: "8px" }}>
                {activeTab !== "archive" && (
                  <button className="mm-archive-btn" onClick={() => setShowArchiveModal(true)}>
                    <i className="fa-solid fa-box-archive" /> Archiver ({selected.length})
                  </button>
                )}
                <button className="mm-delete-btn" onClick={() => setShowDeleteModal(true)} style={{ background: "#EF4444", color: "white", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "500" }}>
                  <i className="fa-solid fa-trash-can" /> Supprimer ({selected.length})
                </button>
              </div>
            )}
          </div>

          <div className="mm-table-wrap">
            {loading ? (
              <div style={{ padding: "60px", textAlign: "center", color: "#94A3B8" }}>
                <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "28px" }} />
              </div>
            ) : (
              <table className="mm-table">
                <thead>
                  <tr>
                    <th className="mm-th-cb">
                      <span className={`mm-cb ${allSelected ? "mm-cb-on" : ""}`} onClick={() => setSelected(allSelected ? [] : paginatedEmails.map((e) => e.id))}>
                        {allSelected && <i className="fa-solid fa-check" />}
                      </span>
                    </th>
                    <th>Nom de l'e-mail</th>
                    <th>Envoyé par</th>
                    <th>Groupe cible</th>
                    <th>Destinataires</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedEmails.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="mm-empty">
                        <i className="fa-regular fa-folder-open" />
                        <p>Aucun e-mail trouvé</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedEmails.map((email) => {
                      // Affichage du groupe avec "Attestés" au lieu de "Diplômés"
                      const displayGroupe = email.groupe_display === "Diplômés" ? "Attestés" : email.groupe_display;
                      return (
                        <tr key={email.id} className={selected.includes(email.id) ? "mm-tr-sel" : ""}>
                          <td className="mm-td-cb">
                            <span className={`mm-cb ${selected.includes(email.id) ? "mm-cb-on" : ""}`} onClick={() => toggleSelect(email.id)}>
                              {selected.includes(email.id) && <i className="fa-solid fa-check" />}
                            </span>
                          </td>
                          <td>
                            <span className="mm-email-name">
                              <span className="mm-email-dot" />
                              {email.objet}
                            </span>
                          </td>
                          <td className="mm-td-gray">{email.envoye_par_email}</td>
                          <td>
                            {email.email_direct ? (
                              <span className="mm-badge" style={{ background: "#e8f5fb", color: "#4aa3c7" }}>
                                <i className="fa-solid fa-at" style={{ marginRight: "4px" }} /> Envoi direct
                              </span>
                            ) : (
                              <span className="mm-badge" data-g={email.groupe}>{displayGroupe}</span>
                            )}
                          </td>
                          <td className="mm-td-num">{(email.nombre_destinataires || 0).toLocaleString()}</td>
                          <td className="mm-td-gray">{email.date}</td>
                          <td>
                            <button className="mm-voir-btn" onClick={() => setDetailEmailId(email.id)}>
                              <i className="fa-solid fa-eye" /> Voir détail
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", padding: "16px 20px", borderTop: "1px solid #F1F5F9" }}>
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="mm-page-btn" style={{ opacity: currentPage === 1 ? 0.4 : 1 }}>
                <i className="fa-solid fa-chevron-left" />
              </button>
              <span style={{ fontSize: "13px", color: "#64748B" }}>Page {currentPage} sur {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="mm-page-btn" style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}>
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          )}
        </div>

        {showArchiveModal && (
          <div className="mm-overlay">
            <div className="mm-modal">
              <div className="mm-modal-ico"><i className="fa-solid fa-box-archive" /></div>
              <h3>Archiver {selected.length} e-mail(s) ?</h3>
              <p>Ces e-mails seront déplacés dans l'onglet Archivé.</p>
              <div className="mm-modal-btns">
                <button className="mm-btn-cancel" onClick={() => setShowArchiveModal(false)}>Annuler</button>
                <button className="mm-btn-ok" onClick={handleArchive}>Confirmer</button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <ConfirmActionModal
            title="Supprimer définitivement"
            message={`Êtes-vous sûr de vouloir supprimer ${selected.length} e-mail(s) ? Cette action est irréversible.`}
            onConfirm={handleDelete}
            onClose={() => setShowDeleteModal(false)}
          />
        )}

        {detailEmailId && (
          <EmailDetailModal emailId={detailEmailId} onClose={() => setDetailEmailId(null)} />
        )}
      </div>
    </Layout>
  );
}